import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import { Plus } from '../../assets/images';
import { membershipAPI, userAPI } from '../../actions/imsServer';
import { RankingCard } from './components/cards';
import { PaperCard } from '../../components/card/PaperCard';
import Popup from '../../components/popup';

// 공통 섹션 컨테이너 스타일
const SectionContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%'
};

export const UserMembership = props => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();

    const [membershipsTotal, setMembershipsTotal] = useState(0);
    const [membershipsData, setMembershipsData] = useState(null);
    const [sortedMemberships, setSortedMemberships] = useState(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [popupSubMessage, setPopupSubMessage] = useState('');
    const [isUnregistering, setIsUnregistering] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [membershipToDelete, setMembershipToDelete] = useState(null);

    const [meInfo, setMeInfo] = useState(JSON.parse(localStorage.getItem('meInfo')));
    const location = useLocation();

    // '탈퇴' 클릭 이벤트 리스너 (메뉴에서 발생한 클릭 감지)
    useEffect(() => {
        const handleUnregisterClick = (e) => {
            // 커스텀 이벤트로 '탈퇴' 클릭 감지
            if (e.detail?.type === 'unregister') {
                setIsUnregistering(prev => !prev);
            }
        };

        window.addEventListener('unregisterClick', handleUnregisterClick);
        return () => {
            window.removeEventListener('unregisterClick', handleUnregisterClick);
        };
    }, []);

    // isUnregistering 상태 변경 시 메뉴에 알림
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('unregisterStateChange', {
            detail: { isUnregistering }
        }));
    }, [isUnregistering]);

    // 데이터 새로고침 함수
    const refreshData = useCallback(async () => {
        try {
            // localStorage에서 최신 meInfo 가져오기
            const storedMeInfo = localStorage.getItem('meInfo');
            if (storedMeInfo) {
                const parsedMeInfo = JSON.parse(storedMeInfo);
                setMeInfo(parsedMeInfo);
            }

            // memberships 다시 로드
            const currentMeInfo = storedMeInfo ? JSON.parse(storedMeInfo) : meInfo;
            if (currentMeInfo?.id) {
                const membershipsResponse = await membershipAPI.getMemberships({
                    userId: currentMeInfo.id,
                    type: 1,
                    state: 0,
                    expand: 'organization'
                });
                if (membershipsResponse?.data) {
                    setMembershipsTotal(membershipsResponse.data.total);
                    setMembershipsData(membershipsResponse.data);
                }
            }
        } catch (error) {
            console.error('Failed to refresh data:', error);
        }
    }, [meInfo]);

    useEffect(() => {
        // Load initial user data from localStorage
        const loadUserDataFromStorage = () => {
            try {
                const meInfo = localStorage.getItem('meInfo');
                setMeInfo(JSON.parse(meInfo));
            } catch (error) {
                console.error('Failed to load user data from localStorage:', error);
            }
        };

        const loadMemberships = async () => {
            if (!meInfo?.id) return;
            const membershipsResponse = await membershipAPI.getMemberships({
                userId: meInfo.id,
                type: 1,
                state: 0,
                expand: 'organization'
            });
            if (membershipsResponse?.data) {
                setMembershipsTotal(membershipsResponse.data.total);
                setMembershipsData(membershipsResponse.data);
            }
        }

        loadUserDataFromStorage();
        loadMemberships();
    }, [meInfo?.id]);

    // memberships를 mainOrganizationId 우선으로 정렬
    useEffect(() => {
        if (membershipsData?.memberships?.length > 0) {
            const sorted = [...membershipsData.memberships].sort((a, b) => {
                const aIsMain = a.organizationId === meInfo.mainOrganizationId;
                const bIsMain = b.organizationId === meInfo.mainOrganizationId;
                if (aIsMain && !bIsMain) return -1;
                if (!aIsMain && bIsMain) return 1;
                return 0;
            });
            setSortedMemberships({ ...membershipsData, memberships: sorted });
        } else {
            setSortedMemberships(null);
        }
    }, [membershipsData, meInfo.mainOrganizationId]);

    // 팝업 표시 함수
    const showPopup = (message, subMessage = '') => {
        setPopupMessage(message);
        setPopupSubMessage(subMessage);
        setPopupOpen(true);
    };

    // 팝업 닫기 함수
    const handleClosePopup = () => {
        setPopupOpen(false);
    };

    // 탈퇴 확인 다이얼로그 열기
    const handleDeleteClick = (membership) => {
        setMembershipToDelete(membership);
        setDeleteDialogOpen(true);
    };

    // 탈퇴 확인 다이얼로그 닫기
    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setMembershipToDelete(null);
    };

    // 탈퇴 실행
    const handleDeleteConfirm = async () => {
        if (!membershipToDelete || !meInfo?.id) return;

        try {
            // 기관 탈퇴
            await membershipAPI.unregisterMembership({
                userId: meInfo.id,
                organizationId: membershipToDelete.organizationId,
            });

            // 탈퇴 후 데이터 다시 로드
            // myInfo 다시 가져오기
            const userResponse = await userAPI.getMyInfo();
            const updatedUserData = userResponse?.data?.user;
            if (updatedUserData) {
                // localStorage에 저장
                localStorage.setItem('meInfo', JSON.stringify(updatedUserData));
                // 상태 업데이트
                setMeInfo(updatedUserData);
            }

            // memberships 다시 로드
            const currentUserId = updatedUserData?.id || meInfo.id;
            const membershipsResponse = await membershipAPI.getMemberships({
                userId: currentUserId,
                type: 1,
                state: 0,
                expand: 'organization'
            });
            if (membershipsResponse?.data) {
                setMembershipsTotal(membershipsResponse.data.total);
                setMembershipsData(membershipsResponse.data);
            }

            setDeleteDialogOpen(false);
            setMembershipToDelete(null);
            setIsUnregistering(false); // 탈퇴 모드 종료
            showPopup('기관에서 탈퇴되었습니다.');
        } catch (error) {
            console.error('기관 탈퇴 실패:', error);
            setDeleteDialogOpen(false);
            setMembershipToDelete(null);
            showPopup('기관 탈퇴에 실패했습니다.', '다시 시도해주세요.');
        }
    };

    return (
        <ContentArea sx={{ padding: '0 10px 20px 10px' }}>
            {/* Main Content */}
            <Box sx={{
                flex: 1,
                ...SectionContainerStyle,
                padding: '0 0px 30px 0px'
            }}>
                {/* Ranking Cards */}
                {sortedMemberships?.memberships && sortedMemberships.memberships.length > 0 ? (
                    sortedMemberships.memberships.map((membership, index) => (
                        <RankingCard
                            key={membership.id || index}
                            membership={membership}
                            userData={meInfo}
                            showDeleteButton={isUnregistering}
                            onDelete={() => handleDeleteClick(membership)}
                            onRefresh={refreshData}
                        />
                    ))
                ) : null}

                {/* Add Organization Card */}
                <PaperCard contentSx={{ padding: 0 }} sx={{ margin: '0px', padding: '0px', justifyContent: 'center', alignItems: 'center' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            height: '382px',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontWeight: 400,
                                color: theme.palette.figma.white[60],
                                letterSpacing: '-0.42px',
                                textAlign: 'center',
                            }}
                        >
                            {t('add_affiliated_organization')}
                        </Typography>
                        <Box
                            sx={{
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            onClick={() =>
                                membershipsTotal >= 5 ? showPopup('소속기관은 최대 5개까지 추가할 수 있습니다.') :
                                    navigate('/auth/organization-search', {
                                        state: { source: 'membership' },
                                    })
                            }
                        >
                            <Plus size={20} />
                        </Box>
                    </Box>
                </PaperCard>
            </Box>

            <Popup
                open={popupOpen}
                message={popupMessage}
                subMessage={popupSubMessage}
                onConfirm={handleClosePopup}
            />

            {/* 탈퇴 확인 다이얼로그 */}
            <Popup
                open={deleteDialogOpen}
                message={`정말 해당기관에서 탈퇴하시겠습니까?`}
                subMessage=""
                onCancel={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                confirmText="탈퇴하기"
            />
        </ContentArea >
    );
};
